import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useApiControl from './common/useApiControl';
import { recoverFromFatalError } from './common/roomFatalError';
import {
    clearQuickJoinNameSetup,
    dismissQuickJoinNameSetup,
    shouldPromptQuickJoinNameSetup,
} from './common/quickJoinNameSetup';
import { clearTopJoinConfirmation } from './common/topJoinConfirmation';
import { useSSEError, useSSETarget } from './common/useSseControl';
import useRoomPhase, {
    getServerPhaseCommandFromSseData,
    ROOM_DISPLAY_PHASE,
    SERVER_PHASE,
} from './components/room/useRoomPhase';
import MemberRoomPhaseContent from './components/room/MemberRoomPhaseContent';
import { normalizeRoomInformationMessages } from './components/room/roomInformationMessages';

import RoomInformation from './components/room/RoomInformation';// SPはボトムに移動した。消すべきか
import NameSetupDialog from './components/dialogs/NameSetupDialog';
import SSEErrorDialog from './components/dialogs/SSEErrorDialog';

import { Box} from "@mui/material";

const RESPONSIVE_MODE = {
    MOBILE: 'mobile',
    DESKTOP: 'desktop',
};

const RESULT_FETCH_STATE = {
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCESS: 'success',
    FAILED: 'failed',
};

const CHAT_POST_COOLDOWN_MS = 3000;
const RECENT_ROUND_RESULT_LIMIT = 10;
const INFORMATION_MESSAGE_TTL_MS = 60 * 1000;

const getInformationMessageTtlMs = (_event, _round) => INFORMATION_MESSAGE_TTL_MS;

const withInformationReceivedAt = (message) => ({
    ...message,
    receivedAt: Date.now(),
});

const pruneExpiredInformationMessages = (messages, ttlMs, now = Date.now()) => (
    messages.filter((message) => (
        typeof message?.receivedAt !== 'number' ||
        now - message.receivedAt <= ttlMs
    ))
);

const getResponsiveMode = () => (
    window.innerWidth <= 500 ? RESPONSIVE_MODE.MOBILE : RESPONSIVE_MODE.DESKTOP
);

const normalizeChatMessages = (messages) => (
    Array.isArray(messages) ? messages.filter((message) => message?.id !== undefined && message?.id !== null) : []
);

const mergeMessagesById = (current, incoming) => {
    const map = new Map();

    [...normalizeChatMessages(current), ...normalizeChatMessages(incoming)].forEach((message) => {
        map.set(Number(message.id), message);
    });

    return Array.from(map.values()).sort((a, b) => Number(a.id) - Number(b.id));
};

const getLatestChatMessageId = (messages) => (
    normalizeChatMessages(messages).reduce((latestId, message) => Math.max(latestId, Number(message.id)), 0)
);

const parseRoundAnswers = (answers) => {
    if (Array.isArray(answers)) return answers;
    if (typeof answers !== 'string' || answers.trim() === '') return [];

    try {
        const parsed = JSON.parse(answers);
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        console.error('Error parsing round answers', error);
        return [];
    }
};

const normalizeRoundResult = (round) => {
    if (!round || round.result === null || round.result === undefined) {
        return null;
    }

    if (round.review_status && round.review_status !== 'confirmed') {
        return null;
    }

    const answers = parseRoundAnswers(round.answers);
    const resultIndex = Number(round.result);

    return {
        number: Number(round.number),
        answers,
        result: round.result,
        resultLabel: Number.isInteger(resultIndex) ? answers[resultIndex] ?? null : null,
    };
};

const getRecentRoundNumbers = (roundNumber) => {
    const currentNumber = Number(roundNumber);

    if (!Number.isInteger(currentNumber) || currentNumber <= 1) {
        return [];
    }

    return Array.from({ length: RECENT_ROUND_RESULT_LIMIT }, (_, index) => currentNumber - index - 1)
        .filter((number) => number > 0);
};

function MemberRoom({ event, round, user, room_id, initialRoundSummary = null }) {
    console.log(`[${new Date().toISOString()}] member room rendered`);

    const sseError = useSSEError();
    const chatMessagesSseData = useSSETarget('chatMessages');
    const eventSseData = useSSETarget('event');
    const importantMessageSseData = useSSETarget('important');
    const infoMessageSseData = useSSETarget('info');
    const roomInformationSseData = useSSETarget('roomInformation');
    const roundSseData = useSSETarget('round');
    const roundStatusSseData = useSSETarget('roundStatus');
    const roundSummarySseData = useSSETarget('roundSummary');
    const [initialized, setInitialized] = useState(false); // 画面遷移直後、表示に最低限必要なデータがそろうまで待つ
    const [event2, setEvent] = useState(event); // 更新されたイベントの状態
    const [round2, setRound] = useState(round);
    const [user2, setUser] = useState(user);
    const [roundResultReviewRefreshKey, setRoundResultReviewRefreshKey] = useState(0);
    const [displayPoint, setDisplayPoint] = useState(user?.point ?? 0);
    const [convertedTags, setConvertedTags] = useState([]);
    const [masterTags, setMasterTags] = useState([]);
    const [formDisplay, setFormDisplay ] = useState(false);
    const [dialogRound, setDialogRound] = useState(event.round);
    const [memberResultContentData, setMemberResultContentData] = useState(null);
    const [resultFetchState, setResultFetchState] = useState(RESULT_FETCH_STATE.IDLE);
    const [isSseErrorDialogVisible, setIsSseErrorDialogVisible] = useState(false);
    const [categoryStatChanges, setCategoryStatChanges] = useState([]);
    const [checkoutSummary, setCheckoutSummary] = useState(null);
    const [pointChangeAmount, setPointChangeAmount] = useState(null);
    const [resultPointChangeAmount, setResultPointChangeAmount] = useState(null);
    const [summaryData, setSummaryData] = useState(initialRoundSummary);
    const [roundResultHistoryByNumber, setRoundResultHistoryByNumber] = useState({});
    const [eventUsersForCheckout, setEventUsersForCheckout] = useState([]);
    const [checkoutEventUsersLoaded, setCheckoutEventUsersLoaded] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [chatPostCooldown, setChatPostCooldown] = useState(false);
    const [isNameSetupDialogVisible, setIsNameSetupDialogVisible] = useState(false);
    const [nameSetupMessage, setNameSetupMessage] = useState('');
    const chatLastIdRef = useRef(null);
    const chatPostCooldownTimerRef = useRef(null);
    const abandonmentCheckoutTriggeredRoundRef = useRef(null);
    const processedSseSeqRef = useRef({});
    const fatalRecoveryStartedRef = useRef(false);


    const [tabIndex, setTabIndex] = useState(0);

    // インフォメーションメッセージの作成中部分
    // 文言を定数で管理（今後の多言語対応や変更にも柔軟）
    const MSG_BEFORE_START = 'もうすぐ次のラウンドが開始します';
    const MSG_STARTED = `ラウンド${round2.number}が開始しました`;
    const MSG_ENDED = 'ベットタイムが終了しました。結果選択中です。';
    const MSG_CHECKED_OUT = 'ルームが終了しました。最終結果を確認できます。'
    const ABANDONMENT_GRACE_MS = 20 * 60 * 1000;

    const statusRef = useRef([
        { message: '🎉 メッセージの配信をお待ちください。', type: 'info' }
    ]);

    const sseRef = useRef([
    //   { message: '⚠️ サーバーは毎日3:00に再起動されます', type: 'info' },
    //   { message: '✅ ラウンド1が正常に終了しました。', type: 'info' },
    //   { message: '📢 現在の接続人数：1,024人', type: 'info' },
    //   { message: '🔥 本日中にメンテナンス告知予定', type: 'important' }
    ]);
    const POPUP_VISIBLE_MS = 3000;
    const POPUP_FADE_MS = 400;
    const popupIdRef = useRef(0);
    const popupTimersRef = useRef([]);
    const betHighlightTimersRef = useRef({});
    const informationPruneTimerRef = useRef(null);
    const betreadyPhaseTriggeredRoundRef = useRef(null);
    const pointChangeShownRoundRef = useRef(null);

    const [infomationMessages, setInfomationMessages] = useState(statusRef.current);
    const [joinPopups, setJoinPopups] = useState([]);
    const [betPopups, setBetPopups] = useState([]);
    const [betPopupUsersBySelection, setBetPopupUsersBySelection] = useState({});
    const [highlightedBetSelections, setHighlightedBetSelections] = useState([]);
    const [betreadyPhaseSignal, setBetreadyPhaseSignal] = useState(0);


    const event_id = parseInt(event.id);

    //レスポンシブ判定
    const [responsiveMode, setResponsiveMode] = useState(getResponsiveMode);
    const confirmedPhaseReady = Boolean(memberResultContentData?.result)
        && Number(memberResultContentData?.roundNumber) === Number(dialogRound - 1);
    const completedPhaseReady = Boolean(checkoutSummary && checkoutEventUsersLoaded);
    const { roomDisplayPhase, timePhase } = useRoomPhase({
        event: event2,
        phaseReadiness: {
            [SERVER_PHASE.CONFIRMED]: confirmedPhaseReady,
            [SERVER_PHASE.COMPLETED]: completedPhaseReady,
        },
        round: round2,
        user: user2,
        sseData: roundStatusSseData,
        betreadyPhaseSignal,
    });
    const recentRoundResultTargetNumbers = useMemo(
        () => getRecentRoundNumbers(round2?.number),
        [round2?.number]
    );
    const recentRoundResults = useMemo(
        () => recentRoundResultTargetNumbers
            .map((number) => roundResultHistoryByNumber[number])
            .filter(Boolean),
        [recentRoundResultTargetNumbers, roundResultHistoryByNumber]
    );
    const missingRecentRoundResultNumbers = useMemo(
        () => recentRoundResultTargetNumbers.filter((number) => !roundResultHistoryByNumber[number]),
        [recentRoundResultTargetNumbers, roundResultHistoryByNumber]
    );
    const missingRecentRoundResultKey = missingRecentRoundResultNumbers.join(',');
    useEffect(() => {
        setDisplayPoint(user2?.point ?? 0);
    }, [user2?.point]);

    useEffect(() => {
        if (initialRoundSummary) {
            setSummaryData(initialRoundSummary);
        }
    }, [initialRoundSummary]);

    useEffect(() => {
        setBetPopupUsersBySelection({});
    }, [round2?.number]);

    useEffect(() => {
        const resultRoundNumber = memberResultContentData?.roundNumber;
        if (
            roomDisplayPhase !== ROOM_DISPLAY_PHASE.CONFIRMED ||
            resultRoundNumber === undefined ||
            resultRoundNumber === null ||
            resultPointChangeAmount === null ||
            pointChangeShownRoundRef.current === resultRoundNumber
        ) {
            return;
        }

        pointChangeShownRoundRef.current = resultRoundNumber;
        setPointChangeAmount(resultPointChangeAmount === 0 ? null : resultPointChangeAmount);
    }, [memberResultContentData?.roundNumber, resultPointChangeAmount, roomDisplayPhase]);

    //メッセージの合体
    //画面遷移直後
    //一定間隔でメッセージを更新
    //ループ内部に変更を連携
    useEffect(() => {
        if (sseError) {
            setIsSseErrorDialogVisible(true);
        }
    }, [sseError]);

    useEffect(() => () => {
        popupTimersRef.current.forEach(clearTimeout);
        popupTimersRef.current = [];
        Object.values(betHighlightTimersRef.current).forEach(clearTimeout);
        betHighlightTimersRef.current = {};
        if (informationPruneTimerRef.current) {
            clearTimeout(informationPruneTimerRef.current);
            informationPruneTimerRef.current = null;
        }
    }, []);

    const shouldProcessSseMessage = useCallback((key, message) => {
        const seq = message?.__seq;
        if (!seq || processedSseSeqRef.current[key] === seq) {
            return false;
        }

        processedSseSeqRef.current[key] = seq;
        return true;
    }, []);

    useEffect(() => {


    const starttime = new Date(round2.starttime);
    const endtime = new Date(round2.endtime);
    const now = new Date();
    const isManualBetClose = event2?.bet_close_mode === 'manual';

    if (round2.starttime === null || now < endtime || isManualBetClose) {
        const intervalId = setInterval(() => {
            // 解散メッセージが表示されたらもう更新しない
            if (statusRef.current[0].type !== 'finally') { 
                const now = new Date();
                // 開始時間未決
                if (round2.starttime === null) {
                    statusRef.current = [{ message: MSG_BEFORE_START, type: 'info' }];
                //開始前
                } else if (now < starttime) {
                    statusRef.current = [{ message: MSG_BEFORE_START, type: 'info' }];
                    //ベットタイム
                } else if (now < endtime || isManualBetClose) {
                    if (betreadyPhaseTriggeredRoundRef.current !== round2.number) {
                        betreadyPhaseTriggeredRoundRef.current = round2.number;
                        setBetreadyPhaseSignal((current) => current + 1);
                    }
                    statusRef.current = [{ message: MSG_STARTED, type: 'info' }];
                    //ベット終了
                } else if (now > endtime) {
                    statusRef.current = [{ message: MSG_ENDED, type: 'info' }];
                    clearInterval(intervalId);
                }
            }

        }, 1000);

        return () => clearInterval(intervalId);
    }
    }, [event2?.bet_close_mode, round2.starttime, round2.endtime, round2.number]);

    useEffect(() => {
        // 新しいラウンドになったらサマリーをリセット
        setSummaryData(initialRoundSummary);
    }, [initialRoundSummary, round2.number]);

    // レスポンシブ判定
    useEffect(() => {
    const handleResize = () => {
        setResponsiveMode(getResponsiveMode());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Join event
    const params1 = useMemo(() => ({
        event_id: event?.id
    }), [event]);

    // get_eventby_room
    const params2 = useMemo(() => ({
        room_id: room_id,
    }), [room_id]);

    // get_round
    const params3 = useMemo(() => ({
        event_id: event2?.id,
        number: event2?.round
    }), [event2]);

    // event_user
    const params4 = useMemo(() => ({
        event_id: event2?.id,
    }), [event2]);

    // Join event
    const { data: data1, loading: loading1, executeRequest: executeRequest1 } = useApiControl({
        action: '/join_event',
        params: params1,
        method: 'POST'
    });

    // get_eventby_room
    const { data: data2, loading: loading2, executeRequest: executeRequest2 } = useApiControl({
        action: '/get_eventby_room',
        params: params2,
        method: 'POST'
    });

    //round
    const { data: data3, loading: loading3, executeRequest: executeRequest3 } = useApiControl({
        action: '/get_round',
        params: params3,
        method: 'POST',
    });

    //event_user
    const { data: data4, loading: loading4, executeRequest: executeRequest4 } = useApiControl({
        action: '/get_event_user',
        params: params4,
        method: 'POST',
    });

    // マスタータグ取得
    const { data: data5, loading: loading5, executeRequest: executeRequest5 } = useApiControl({
        action: '/get_master_tags',
        method: 'POST',
    });
    useEffect(() => {
        const localTags = localStorage.getItem('tags');
        if (localTags) {
        try {
            const parsedTags = JSON.parse(localTags);
            setMasterTags(parsedTags);
        } catch (e) {
            console.error('ローカルストレージのtagsパースエラー', e);
            executeRequest5();
        }
        } else {
            executeRequest5();
        }
    }, []);
    
    // get my result
    const params6 = useMemo(() => ({
        event_id: event?.id,
        number: dialogRound
    }), [dialogRound]);

    const { data: data6, loading: loading6, executeRequest: executeRequest6 } = useApiControl({
        action: '/get_round_result',
        params: params6,
        method: 'POST',
    });
    const { executeRequest: executeGetRoundResultsByNumbers } = useApiControl({
        action: '/get_round_results_by_numbers',
        method: 'POST',
    });
    const { loading: updatingName, executeRequest: executeUpdateName } = useApiControl({
        action: '/update_user',
        method: 'POST',
    });

    const openNameSetupDialogIfNeeded = useCallback(() => {
        if (!shouldPromptQuickJoinNameSetup(user2)) {
            return;
        }

        setNameSetupMessage('');
        setIsNameSetupDialogVisible(true);
    }, [user2]);

    const closeNameSetupDialogForLater = useCallback(() => {
        dismissQuickJoinNameSetup(user2);
        setIsNameSetupDialogVisible(false);
        setNameSetupMessage('');
    }, [user2]);

    const saveNameSetup = useCallback((name) => {
        setNameSetupMessage('');
        executeUpdateName({ name })
            .then((response) => {
                const updatedUser = response?.user ?? { ...user2, name };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
                clearQuickJoinNameSetup(updatedUser);
                setIsNameSetupDialogVisible(false);
            })
            .catch((error) => {
                console.error(error);
                const validationName = error.response?.data?.errors?.name;
                setNameSetupMessage(
                    Array.isArray(validationName)
                        ? validationName[0]
                        : error.response?.data?.message || '表示名を保存できませんでした。'
                );
            });
    }, [executeUpdateName, user2]);

    const mergeRoundResults = useCallback((roundResults) => {
        if (!Array.isArray(roundResults) || roundResults.length === 0) {
            return;
        }

        setRoundResultHistoryByNumber((current) => {
            const next = { ...current };

            roundResults.forEach((roundResult) => {
                const normalized = normalizeRoundResult({
                    ...roundResult,
                    review_status: roundResult.review_status ?? 'confirmed',
                    answers: roundResult.answers,
                    result: roundResult.result,
                    number: roundResult.number,
                });

                if (normalized && Number.isInteger(normalized.number)) {
                    next[normalized.number] = normalized;
                }
            });

            return next;
        });
    }, []);

    useEffect(() => {
        const normalized = normalizeRoundResult(round2);

        if (!normalized || !Number.isInteger(normalized.number)) {
            return;
        }

        setRoundResultHistoryByNumber((current) => ({
            ...current,
            [normalized.number]: normalized,
        }));
    }, [round2?.answers, round2?.number, round2?.result, round2?.review_status]);

    useEffect(() => {
        if (!event_id || missingRecentRoundResultKey === '') {
            return;
        }

        const roundNumbers = missingRecentRoundResultKey.split(',').map(Number).filter((number) => number > 0);

        if (roundNumbers.length === 0) {
            return;
        }

        executeGetRoundResultsByNumbers({
            event_id,
            round_numbers: roundNumbers,
        }).then((response) => {
            mergeRoundResults(response?.results);
        }).catch((error) => {
            console.error(error);
        });
    }, [event_id, executeGetRoundResultsByNumbers, mergeRoundResults, missingRecentRoundResultKey]);

    useEffect(() => {
        if (!data6) {
            return;
        }

        const result = data6;
        const currentUserId = user2.id;

        const userResults = Array.isArray(result.users)
            ? result.users.filter((r) => r.user_id === currentUserId)
            : [];
        let nextPointChangeAmount = 0;

        if (userResults.length > 0) {
            const totalBet = userResults.reduce((sum, r) => sum + (r.bet ?? 0), 0);
            const totalRefund = userResults.reduce((sum, r) => sum + (r.refund_point ?? 0), 0);
            nextPointChangeAmount = totalRefund - totalBet;
        } else {
            const eventUser = Array.isArray(result.event_users)
                ? result.event_users.find((eu) => eu.user_id === currentUserId)
                : null;
            if (eventUser && typeof eventUser.balance === 'number') {
                nextPointChangeAmount = eventUser.balance;
            }
        }
        setResultPointChangeAmount(nextPointChangeAmount);

        setDialogRound((prev) => prev + 1);
        setMemberResultContentData({
            result: data6, // contains event_users now
            event_id: event2.id,
            roundNumber: dialogRound,
        });
        setResultFetchState(RESULT_FETCH_STATE.SUCCESS);
        openNameSetupDialogIfNeeded();
    }, [data6]);

    // checkout_event_result 
    const params7 = useMemo(() => ({
        event_id: event2?.id,
    }), [event2]);

    const params8 = useMemo(() => ({
        event_id: event2?.id,
        max: 100,
    }), [event2]);

    const { data: data7, loading: loading7, executeRequest: executeRequest7 } = useApiControl({
        action: '/checkout_event_result',
        params: params7,
        method: 'POST',
    });

    const { data: data8, executeRequest: executeRequest8 } = useApiControl({
        action: '/get_event_users',
        params: params8,
        method: 'POST',
    });

    const { loading: chatLoading, executeRequest: executeGetChatMessages } = useApiControl({
        action: '/get_chat_messages',
        method: 'POST',
    });

    const { executeRequest: executeGetChatUpdates } = useApiControl({
        action: '/get_chat_updates',
        method: 'POST',
    });

    const { loading: chatPosting, executeRequest: executePostChatMessage } = useApiControl({
        action: '/post_chat_message',
        method: 'POST',
    });

    useEffect(() => {
        if (data7) {
            setCheckoutSummary(data7);
            if (Array.isArray(data7.category_stats)) {
                setCategoryStatChanges(data7.category_stats);
            } else {
                setCategoryStatChanges([]);
            }
            setCheckoutEventUsersLoaded(false);
            executeRequest8().catch((error) => {
                console.error(error);
            });
            updateUser();
        }
    }, [data7]);

    useEffect(() => {
        const waitingPhases = new Set([
            ROOM_DISPLAY_PHASE.BET_COMPLETION,
            ROOM_DISPLAY_PHASE.LOCKED,
            ROOM_DISPLAY_PHASE.RESULT_PENDING,
        ]);
        if (!waitingPhases.has(roomDisplayPhase) || !event2?.id) {
            return undefined;
        }

        const currentRoundIsUnused =
            !round2?.starttime &&
            !round2?.endtime &&
            round2?.result == null &&
            round2?.result_rate == null;
        const actualBetCloseTime =
            event2?.bet_close_mode === 'manual' ? round2?.bet_closed_at : round2?.endtime;
        const baseTime =
            actualBetCloseTime ??
            (currentRoundIsUnused ? round2?.previous_round_confirmed_at : null);

        if (!baseTime) {
            return undefined;
        }

        const baseTimeMs = new Date(baseTime).getTime();
        if (Number.isNaN(baseTimeMs)) {
            return undefined;
        }

        const waitKind = actualBetCloseTime ? 'roundEnd' : 'nextAction';
        const roundKey = `${event2.id}:${round2.number ?? round2.id}:${waitKind}`;
        const delayMs = Math.max(baseTimeMs + ABANDONMENT_GRACE_MS - Date.now(), 0);
        const timeoutId = setTimeout(() => {
            if (abandonmentCheckoutTriggeredRoundRef.current === roundKey) {
                return;
            }
            abandonmentCheckoutTriggeredRoundRef.current = roundKey;
            executeRequest7().catch(() => {
                abandonmentCheckoutTriggeredRoundRef.current = null;
                updateEvent();
            });
        }, delayMs);

        return () => clearTimeout(timeoutId);
    }, [
        roomDisplayPhase,
        round2?.id,
        round2?.number,
        round2?.starttime,
        round2?.endtime,
        round2?.bet_closed_at,
        round2?.result,
        round2?.result_rate,
        round2?.previous_round_confirmed_at,
        event2?.id,
        executeRequest7,
    ]);


    useEffect(() => {
        if (!data8) return;
        if (Array.isArray(data8.all)) {
            setEventUsersForCheckout(data8.all);
        }
        setCheckoutEventUsersLoaded(true);
    }, [data8]);

    const chatRoomId = event2?.id ?? event_id;

    const replaceChatMessages = useCallback((incomingMessages) => {
        const nextMessages = mergeMessagesById([], incomingMessages);
        chatLastIdRef.current = nextMessages.length > 0 ? getLatestChatMessageId(nextMessages) : null;
        setChatMessages(nextMessages);
    }, []);

    const appendChatMessages = useCallback((incomingMessages) => {
        const normalizedIncoming = normalizeChatMessages(incomingMessages);
        if (normalizedIncoming.length === 0) {
            return;
        }

        setChatMessages((currentMessages) => {
            const nextMessages = mergeMessagesById(currentMessages, normalizedIncoming);
            chatLastIdRef.current = nextMessages.length > 0 ? getLatestChatMessageId(nextMessages) : null;
            return nextMessages;
        });
    }, []);

    const countUnreadChatMessages = useCallback((incomingMessages) => {
        const normalizedIncoming = normalizeChatMessages(incomingMessages);
        if (normalizedIncoming.length === 0) {
            return;
        }

        const hasOtherUserMessage = normalizedIncoming.some((message) => (
            String(message.user_id) !== String(user2?.id)
        ));

        if (hasOtherUserMessage) {
            setUnreadChatCount((currentCount) => Math.min(currentCount + normalizedIncoming.length, 99));
        }
    }, [user2?.id]);

    const clearUnreadChatCount = useCallback(() => {
        setUnreadChatCount(0);
    }, []);

    const fetchChatUpdates = useCallback(async () => {
        if (!chatRoomId) {
            return;
        }

        try {
            const result = await executeGetChatUpdates({
                chat_room_id: chatRoomId,
                last_id: chatLastIdRef.current ?? 0,
            });
            countUnreadChatMessages(result?.messages);
            appendChatMessages(result?.messages);
        } catch (error) {
            console.error('チャット差分取得に失敗しました。', error);
        }
    }, [appendChatMessages, chatRoomId, countUnreadChatMessages, executeGetChatUpdates]);

    const startChatPostCooldown = useCallback(() => {
        if (chatPostCooldownTimerRef.current) {
            clearTimeout(chatPostCooldownTimerRef.current);
        }

        setChatPostCooldown(true);
        chatPostCooldownTimerRef.current = setTimeout(() => {
            setChatPostCooldown(false);
            chatPostCooldownTimerRef.current = null;
        }, CHAT_POST_COOLDOWN_MS);
    }, []);

    const postChatMessage = useCallback(async (message) => {
        if (chatPosting || chatPostCooldown) {
            return false;
        }

        if (!chatRoomId) {
            return false;
        }

        const trimmedMessage = message.trim();
        if (!trimmedMessage) {
            return false;
        }

        try {
            const result = await executePostChatMessage({
                chat_room_id: chatRoomId,
                message: trimmedMessage,
            });

            if (result?.message) {
                appendChatMessages([result.message]);
            } else {
                await fetchChatUpdates();
            }

            startChatPostCooldown();
            openNameSetupDialogIfNeeded();
            return true;
        } catch (error) {
            console.error('チャット投稿に失敗しました。', error);
            return false;
        }
    }, [appendChatMessages, chatPostCooldown, chatPosting, chatRoomId, executePostChatMessage, fetchChatUpdates, openNameSetupDialogIfNeeded, startChatPostCooldown]);

    useEffect(() => () => {
        if (chatPostCooldownTimerRef.current) {
            clearTimeout(chatPostCooldownTimerRef.current);
        }
    }, []);

    useEffect(() => {
        if (!chatRoomId) {
            replaceChatMessages([]);
            return;
        }

        executeGetChatMessages({
            chat_room_id: chatRoomId,
            limit: 50,
        }).then((result) => {
            replaceChatMessages(result?.messages);
        }).catch((error) => {
            console.error('チャット初期取得に失敗しました。', error);
        });
    }, [chatRoomId, executeGetChatMessages, replaceChatMessages]);

    useEffect(() => {
        if (
            !shouldProcessSseMessage('chatMessages', chatMessagesSseData) ||
            chatMessagesSseData?.type !== 'system' ||
            chatMessagesSseData.action !== 'sync' ||
            chatMessagesSseData.target !== 'chatMessages'
        ) {
            return;
        }

        const latestId = Number(chatMessagesSseData?.payload?.latest_id);
        if (
            Number.isInteger(latestId) &&
            chatLastIdRef.current !== null &&
            latestId <= chatLastIdRef.current
        ) {
            return;
        }

        fetchChatUpdates();
    }, [chatMessagesSseData, fetchChatUpdates, shouldProcessSseMessage]);

    const updateEvent = async () => {
        try {
            const result = await executeRequest2();
            if (result?.event) {
                // get_round を新しいイベント情報を元に即座に実行
                await executeRequest3({
                    event_id: result.event.id,
                    number: result.event.round
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const updateRound = () => {
        executeRequest3().catch((error) => {
            console.error(error);
            // setExceptionOccurred(true);
        });
    };

    const updateUser = () => {
        executeRequest4().catch((error) => {
            console.error(error);
            // setExceptionOccurred(true);
        });
    };

    const updateMasterTags = () => {
        localStorage.removeItem('tags');
        executeRequest5().catch((error) => {
            console.error(error);
        });
    };

    //event取得をトリガーとする後続処理
    useEffect(() => {
        if (data2) {
            setEvent(data2.event);
        }
    }, [data2]);

    useEffect(() => {
        if (data3) {
            if (
                data3.id !== round2?.id ||
                data3.starttime !== round2?.starttime ||
                data3.endtime !== round2?.endtime
            ) {
                setRound(data3);
            }
        }
    }, [data3, round2]);

    useEffect(() => {
        if (!memberResultContentData?.result?.round_not_formed || !round2?.starttime) {
            return;
        }

        setMemberResultContentData(null);
        setResultFetchState(RESULT_FETCH_STATE.IDLE);
    }, [memberResultContentData?.result?.round_not_formed, round2?.id, round2?.starttime]);
    
    useEffect(() => {
        if (data4) {
            setUser(data4.user);
        }
    }, [data4]);

    // 新規参加の場合、roomにユーザー登録されていないので、参加確定後ユーザー情報を改めて取得する
    useEffect(() => {
        if (data1) {
            clearTopJoinConfirmation(room_id);
        }

        if (!user && data1 && ['joined', 'already_joined'].includes(data1.status)) {
            updateUser();
        }
    }, [user, data1, room_id]);

    useEffect(() => {
        if (data5) {
            localStorage.setItem('tags', JSON.stringify(data5.tags));
            setMasterTags(data5.tags);
        }
    }, [data5]);
    
    const [exceptionOccurred, setExceptionOccurred] = useState(false);

    useEffect(() => {
            // エラー発生時は再実行しない
        if (params1 && !exceptionOccurred) {
            executeRequest1().catch((error) => {
                console.error(error);
                clearTopJoinConfirmation(room_id);
                setExceptionOccurred(true);
            });
        }
    }, [params1]);

    useEffect(() => {
        if (!exceptionOccurred || fatalRecoveryStartedRef.current) {
            return;
        }

        fatalRecoveryStartedRef.current = true;
        recoverFromFatalError({
            loggedInMessage: 'ルーム参加処理中にエラーが発生しました。トップページに戻ります。',
            loggedOutMessage: 'ルーム参加処理中にエラーが発生しました。ログインの有効期限が切れた可能性があります。もう一度ログインしてください。',
            redirectTo: './',
            loggedOutRedirectTo: './',
        });
    }, [exceptionOccurred, room_id]);

    // sse チェックここから
    // system: update (round)
    useEffect(() => {
        if (
            shouldProcessSseMessage('round', roundSseData) &&
            roundSseData?.type === 'system' &&
            roundSseData.action === 'resync' &&
            roundSseData.target === 'round'
        ) {
            updateRound();
        }
    }, [roundSseData, shouldProcessSseMessage]);

    // system: update (event)
    useEffect(() => {
        if (
            shouldProcessSseMessage('event', eventSseData) &&
            eventSseData?.type === 'system' &&
            eventSseData.action === 'resync' &&
            eventSseData.target === 'event'
        ) {
            updateEvent();
        }
    }, [eventSseData, shouldProcessSseMessage]);

    //test
    useEffect(() => {
        console.log(event2);

    }, [event2]);

    // status command
    useEffect(() => {
        if (!shouldProcessSseMessage('roundStatus', roundStatusSseData)) {
            return;
        }

        const commandPhase = getServerPhaseCommandFromSseData(roundStatusSseData);
        const commandRoundNumber = Number(roundStatusSseData?.payload?.round_number);
        const hasCommandRoundNumber = Number.isInteger(commandRoundNumber) && commandRoundNumber > 0;

        switch (commandPhase) {
            case SERVER_PHASE.BETREADY:
                if (!memberResultContentData?.result?.round_not_formed) {
                    setMemberResultContentData(null);
                    setResultFetchState(RESULT_FETCH_STATE.IDLE);
                }
                setPointChangeAmount(null);
                setResultPointChangeAmount(null);
                updateUser();
                if (hasCommandRoundNumber) {
                    executeRequest3({
                        event_id: event2.id,
                        number: commandRoundNumber
                    }).catch((error) => {
                        console.error(error);
                    });
                }
                updateEvent();
                break;
            case SERVER_PHASE.LOCKED:
                updateRound();
                updateUser();
                break;
            case SERVER_PHASE.RESULT_PENDING:
                setRoundResultReviewRefreshKey((prev) => prev + 1);
                updateRound();
                break;
            case SERVER_PHASE.CONFIRMED:
                setMemberResultContentData(null);
                setResultFetchState(RESULT_FETCH_STATE.LOADING);
                setPointChangeAmount(null);
                setResultPointChangeAmount(null);
                executeRequest6()
                    .then((result) => {
                        if (!result) {
                            setResultFetchState(RESULT_FETCH_STATE.FAILED);
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        setResultFetchState(RESULT_FETCH_STATE.FAILED);
                    });
                updateUser();
                updateEvent();
                break;
            case SERVER_PHASE.COMPLETED:
                setCheckoutSummary(null);
                setCheckoutEventUsersLoaded(false);
                executeRequest7().then(() => {
                    updateUser();
                }).catch((error) => {
                    console.error(error);
                });
                statusRef.current = [{ message: MSG_CHECKED_OUT, type: 'finally' }]; // ルーム解散メッセージ
                break;
            default:
                break;
        }
    }, [memberResultContentData?.result?.round_not_formed, roundStatusSseData, shouldProcessSseMessage]);

    const normalizeInformationPayload = (payload, type) => {
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
            return {
                message: payload.message ?? payload.text ?? '',
                type,
                user: payload.user ?? null,
                users: Array.isArray(payload.users) ? payload.users : [],
            };
        }

        return { message: payload, type };
    };

    const informationMessageTtlMs = useMemo(
        () => getInformationMessageTtlMs(event2, round2),
        [event2, round2]
    );

    const refreshInformationMessages = useCallback(() => {
        sseRef.current = pruneExpiredInformationMessages(sseRef.current, informationMessageTtlMs);
        const status = statusRef.current[0];
        const sse = sseRef.current.slice(0, 4);
        setInfomationMessages([status, ...sse]);
    }, [informationMessageTtlMs]);

    const addInformationMessages = useCallback((messages) => {
        const nextMessages = (Array.isArray(messages) ? messages : [messages])
            .filter((message) => message?.message)
            .map(withInformationReceivedAt);

        if (nextMessages.length === 0) {
            return;
        }

        nextMessages.forEach((message) => {
            sseRef.current.unshift(message);
        });
        sseRef.current = sseRef.current.slice(0, 50);

        refreshInformationMessages();
    }, [refreshInformationMessages]);

    useEffect(() => {
        const nextExpiresIn = sseRef.current.reduce((minRemainingMs, message) => {
            if (typeof message?.receivedAt !== 'number') {
                return minRemainingMs;
            }

            const remainingMs = message.receivedAt + informationMessageTtlMs - Date.now();
            return Math.min(minRemainingMs, Math.max(remainingMs, 0));
        }, Infinity);

        if (!Number.isFinite(nextExpiresIn)) {
            return undefined;
        }

        if (informationPruneTimerRef.current) {
            clearTimeout(informationPruneTimerRef.current);
        }

        informationPruneTimerRef.current = setTimeout(() => {
            informationPruneTimerRef.current = null;
            refreshInformationMessages();
        }, nextExpiresIn + 50);

        return () => {
            if (informationPruneTimerRef.current) {
                clearTimeout(informationPruneTimerRef.current);
                informationPruneTimerRef.current = null;
            }
        };
    }, [infomationMessages, informationMessageTtlMs, refreshInformationMessages]);

    useEffect(() => {
        sseRef.current = [];
        refreshInformationMessages();
    }, [round2?.number, refreshInformationMessages]);

    useEffect(() => {
        refreshInformationMessages();
    }, [roomDisplayPhase, timePhase, refreshInformationMessages]);

    const addTimedPopups = (items, setter) => {
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        const nextItems = items.map((item) => ({
            ...item,
            id: `${Date.now()}-${popupIdRef.current++}`,
        }));

        setter((prev) => [...nextItems, ...prev].slice(0, 20));

        const fadeTimeoutId = setTimeout(() => {
            const ids = new Set(nextItems.map((item) => item.id));
            setter((prev) => prev.map((item) => (
                ids.has(item.id) ? { ...item, exiting: true } : item
            )));
        }, POPUP_VISIBLE_MS);

        const removeTimeoutId = setTimeout(() => {
            const ids = new Set(nextItems.map((item) => item.id));
            setter((prev) => prev.filter((item) => !ids.has(item.id)));
        }, POPUP_VISIBLE_MS + POPUP_FADE_MS);

        popupTimersRef.current.push(fadeTimeoutId, removeTimeoutId);
    };

    const triggerBetSelectionHighlight = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        const selections = [...new Set(
            items
                .map((item) => Number(item?.selection))
                .filter((selection) => Number.isInteger(selection) && selection >= 0)
        )];

        if (selections.length === 0) {
            return;
        }

        setHighlightedBetSelections((current) => [...new Set([...current, ...selections])]);

        selections.forEach((selection) => {
            if (betHighlightTimersRef.current[selection]) {
                clearTimeout(betHighlightTimersRef.current[selection]);
            }

            betHighlightTimersRef.current[selection] = setTimeout(() => {
                setHighlightedBetSelections((current) => current.filter((item) => item !== selection));
                delete betHighlightTimersRef.current[selection];
            }, POPUP_VISIBLE_MS);
        });
    };

    const updateBetPopupUsersBySelection = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return;
        }

        setBetPopupUsersBySelection((current) => {
            const next = Object.entries(current || {}).reduce((acc, [selection, users]) => {
                acc[selection] = Array.isArray(users) ? [...users] : [];
                return acc;
            }, {});

            items.forEach((item) => {
                const name = String(item?.name || '').trim();
                const selection = Number(item?.selection);
                if (!name || !Number.isInteger(selection) || selection < 0) {
                    return;
                }

                // TODO: betPopups に user_id が含まれるようになったら、name ではなく user_id で置換する。
                // 現状は SSE payload を変更しない小規模対応として、ユニークなユーザー名を暫定キーにしている。
                Object.keys(next).forEach((key) => {
                    next[key] = next[key].filter((user) => user.name !== name);
                    if (next[key].length === 0) {
                        delete next[key];
                    }
                });

                const key = String(selection);
                next[key] = [
                    {
                        name,
                        selection,
                        created_at: item.created_at,
                    },
                    ...(next[key] || []),
                ];
            });

            return next;
        });
    };

    const handleNoticeMessage = useCallback((message) => {
        if (
            !shouldProcessSseMessage(`notice:${message?.target}`, message) ||
            message?.type !== 'message' ||
            message.action !== 'notify' ||
            !['info', 'important'].includes(message.target)
        ) {
            return;
        }

        addInformationMessages(normalizeInformationPayload(message.payload, message.target));
    }, [addInformationMessages, shouldProcessSseMessage]);

    // message: info または important は下部インフォメーションメッセージへ流す
    useEffect(() => {
        handleNoticeMessage(infoMessageSseData);
    }, [handleNoticeMessage, infoMessageSseData]);

    useEffect(() => {
        handleNoticeMessage(importantMessageSseData);
    }, [handleNoticeMessage, importantMessageSseData]);

    // message: roomInformationは表示先ごとに振り分ける
    useEffect(() => {
        if (
            shouldProcessSseMessage('roomInformation', roomInformationSseData) &&
            roomInformationSseData?.type === 'message' &&
            roomInformationSseData.action === 'notify' &&
            roomInformationSseData.target === 'roomInformation'
        ) {
            const payload = roomInformationSseData.payload || {};
            const informationMessages = normalizeRoomInformationMessages(
                payload.messages,
                responsiveMode === RESPONSIVE_MODE.MOBILE,
            );

            addInformationMessages(informationMessages);

            addTimedPopups(payload.joinPopups, setJoinPopups);
            addTimedPopups(payload.betPopups, setBetPopups);
            updateBetPopupUsersBySelection(payload.betPopups);
            triggerBetSelectionHighlight(payload.betPopups);
        }
    }, [addInformationMessages, responsiveMode, roomInformationSseData, shouldProcessSseMessage]);

    // direct: update (roundSummary)
    useEffect(() => {
        if (
            shouldProcessSseMessage('roundSummary', roundSummarySseData) &&
            roundSummarySseData?.type === 'direct' &&
            roundSummarySseData.action === 'update' &&
            roundSummarySseData.target === 'roundSummary'
        ) {
            setSummaryData(roundSummarySseData.payload);
        }
    }, [roundSummarySseData, shouldProcessSseMessage]);

    // sse チェックここまで

    // 画面遷移直後、APIからのレスポンスがまだない場合は何も表示しない
    useEffect(() => {
        if (loading1 || loading2 || loading3 || loading4 || loading5 || exceptionOccurred) {
            setInitialized(true);
        }

    }, [loading1, loading2, loading3, loading4, loading5, exceptionOccurred])


    // 画面遷移直後、APIからのレスポンスがまだない場合は何も表示しない
    if (initialized == false) {
        return (
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    zIndex: '1001',
                }}
            >
                <RoomInformation messages={infomationMessages} />
            </Box>
        );
    }

    // エラーが発生した場合は復旧処理へ
    if (exceptionOccurred) {
        return null;
    }

    const updateFormDisplay = (bool) => {
        setFormDisplay(bool);
    };

    //そのうち消す
    const handleChange = (event, newIndex) => {
        setTabIndex(newIndex);
    };

    const renderPhaseContent = ({ responsiveMode }) => {
        const statusContentProps = {
            checkoutContent: {
                dialogDatas: {
                    user: user2,
                    event: event2,
                    categoryStats: categoryStatChanges,
                    masterTags,
                    checkout: checkoutSummary,
                    eventUsers: eventUsersForCheckout,
                },
            },
            pendingResultContent: {
                refreshKey: roundResultReviewRefreshKey,
                round: round2,
            },
            resultContent: {
                dialogDatas: memberResultContentData,
                resultFetchState,
                user: user2,
            },
        };
        const chatProps = {
            currentUserId: user2?.id,
            event: event2,
            loading: chatLoading,
            messages: chatMessages,
            onSubmit: postChatMessage,
            postCooldown: chatPostCooldown,
            posting: chatPosting,
        };

        const layoutProps = {
            betPopups,
            displayPoint,
            event: event2,
            infomationMessages,
            joinPopups,
            pointChangeAmount,
            round: round2,
            updateRound,
            updateUser,
            user: user2,
        };
        const viewProps = {
            chatProps,
            displayPoint,
            event: event2,
            eventId: event_id,
            masterTags,
            responsiveMode,
            round: round2,
            recentRoundResultTargetNumbers,
            recentRoundResults,
            highlightedBetSelections,
            betPopupUsersBySelection,
            onOpenChat: clearUnreadChatCount,
            setDisplayPoint,
            setPointChangeAmount,
            setTabIndex,
            summaryData,
            tabIndex,
            timePhase,
            unreadChatCount,
            updateMasterTags,
            updateUser,
            user: user2,
        };
        const displayRoomPhase = memberResultContentData?.result?.round_not_formed &&
            roomDisplayPhase === ROOM_DISPLAY_PHASE.BETREADY &&
            !round2?.starttime
            ? ROOM_DISPLAY_PHASE.CONFIRMED
            : roomDisplayPhase;

        return (
            <MemberRoomPhaseContent
                layoutProps={layoutProps}
                roomDisplayPhase={displayRoomPhase}
                responsiveMode={responsiveMode}
                statusContentProps={statusContentProps}
                timePhase={timePhase}
                viewProps={viewProps}
            />
        );
    };


    if (data1 && round2 && user2) {
        return (
            <>
                {/* dialog */}
                <SSEErrorDialog isVisible={isSseErrorDialogVisible} />
                <NameSetupDialog
                    isVisible={isNameSetupDialogVisible}
                    initialName={user2?.name}
                    loading={updatingName}
                    message={nameSetupMessage}
                    onSave={saveNameSetup}
                    onLater={closeNameSetupDialogForLater}
                />
                {renderPhaseContent({ responsiveMode })}
            </>
        );
    }
}

export default MemberRoom;
